import React, { useContext, useEffect, useMemo, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import moment from "moment";
import DatePicker from "react-datepicker";
import { Bar } from "react-chartjs-2";
import { Loader } from "semantic-ui-react";
import useAxios from "axios-hooks";

// History > Power curve (2026-10-24 request, modelled on a reference SCADA
// screenshot): for a date range, the turbine's minute snapshots binned by
// wind speed - bars = how many minutes fell in each 0.1 m/s bin ("Logs"),
// line = their average power. Binning is done server-side
// (Orbital-server routes/historyData.js /powerCurve), which also leaves out
// minutes with an active error code.

const DEFAULT_DAYS = 30;
// A bin's average power from a handful of minutes is mostly noise - the
// line skips those bins (spanGaps bridges them), the bars still show them.
const MIN_LOGS_FOR_POWER = 3;
const API_DATE = "YYYY-MM-DD";
// Server-side limit (POWER_CURVE_MAX_DAYS) - the start picker enforces it.
const MAX_DAYS = 366;

const Wrapper = styled.div`
  position: relative;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 24px;
  margin-bottom: 24px;
  color: ${(props) => props.theme.textDark};

  input {
    background-color: ${(props) => props.theme.controlsBackgroundColor};
    color: ${(props) => props.theme.textDark};
    border: 0;
    border-radius: 8px;
    padding: 0.6rem 1rem;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: ${(props) => props.theme.textMedium};
`;

const Message = styled.div`
  padding: 3rem 0;
  text-align: center;
  color: ${(props) => props.theme.textMedium};
`;

const findParam = (params, dbname) =>
  (params || []).find((p) => p.dbname && p.dbname.toLowerCase() === dbname);

const PowerCurve = ({ windTurbine, monitoringParams }) => {
  const theme = useContext(ThemeContext);
  const [endDate, setEndDate] = useState(new Date());
  const [startDate, setStartDate] = useState(
    moment().subtract(DEFAULT_DAYS, "days").toDate()
  );

  const windParam = findParam(monitoringParams, "wind");
  const powerParam = findParam(monitoringParams, "power");

  const [{ data, loading, error }, fetchCurve] = useAxios(
    {
      url: `/historyData/powerCurve`,
      params: {
        windTurbineId: windTurbine.id,
        start: moment(startDate).format(API_DATE),
        end: moment(endDate).format(API_DATE),
        windScale: (windParam && windParam.scale) || 1,
        powerScale: (powerParam && powerParam.scale) || 1,
      },
    },
    { manual: true }
  );

  useEffect(() => {
    fetchCurve().catch(() => {});
    // eslint-disable-next-line
  }, [windTurbine.id, startDate, endDate]);

  const chart = useMemo(() => {
    const bins = (data && data.bins) || [];
    if (bins.length === 0) return null;

    // Every bin from 0 m/s up to the highest one with data, so the x axis
    // is linear in wind speed even where some bins are empty.
    const binWidth = data.binWidth || 0.1;
    const byIndex = {};
    bins.forEach((b) => {
      byIndex[Math.round(b.wind / binWidth)] = b;
    });
    const lastIndex = Math.max(...Object.keys(byIndex).map(Number));
    const labels = [];
    const logs = [];
    const power = [];
    for (let i = 0; i <= lastIndex; i++) {
      const bin = byIndex[i];
      labels.push((i * binWidth).toFixed(1));
      logs.push(bin ? bin.count : 0);
      power.push(
        bin && bin.count >= MIN_LOGS_FOR_POWER && bin.power !== null
          ? bin.power
          : null
      );
    }

    const name = windTurbine.name || "";
    const powerUnit = (powerParam && powerParam.unit) || "kW";
    const windUnit = (windParam && windParam.unit) || "m/s";
    const axisTicks = { fontColor: theme.textMedium };
    const gridLines = {
      drawBorder: false,
      color: theme.controlsBackgroundColor,
      zeroLineColor: theme.controlsBackgroundColor,
    };

    return {
      data: {
        labels,
        datasets: [
          {
            type: "line",
            label: `${name} Power (${powerUnit})`,
            unit: powerUnit,
            data: power,
            yAxisID: "y-power",
            borderColor: theme.graphAxle1,
            backgroundColor: theme.graphAxle1,
            fill: false,
            pointRadius: 0,
            borderWidth: 2,
            lineTension: 0,
            spanGaps: true,
          },
          {
            type: "bar",
            label: `${name} Logs`,
            unit: "",
            data: logs,
            yAxisID: "y-logs",
            backgroundColor: theme.graphAxle2,
            borderColor: theme.graphAxle2,
          },
        ],
      },
      options: {
        animation: { duration: 0 },
        legend: { labels: { fontColor: theme.textDark } },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: (items) => (items.length ? `${items[0].label} ${windUnit}` : ""),
            label: (item, chartData) => {
              const ds = chartData.datasets[item.datasetIndex];
              const value = Number.isFinite(item.yLabel)
                ? Math.round(item.yLabel * 10) / 10
                : "-";
              return `${ds.label}: ${value} ${ds.unit}`.trim();
            },
          },
        },
        scales: {
          xAxes: [
            {
              categoryPercentage: 1,
              barPercentage: 0.8,
              gridLines: { display: false },
              ticks: {
                ...axisTicks,
                autoSkip: false,
                maxRotation: 45,
                // One label per 2 m/s, like the reference.
                callback: (value, index) =>
                  index % Math.round(2 / binWidth) === 0
                    ? `${Math.round(index * binWidth)} ${windUnit}`
                    : null,
              },
            },
          ],
          yAxes: [
            {
              id: "y-logs",
              position: "left",
              gridLines,
              ticks: { ...axisTicks, beginAtZero: true, precision: 0 },
            },
            {
              id: "y-power",
              position: "right",
              gridLines: { display: false },
              ticks: axisTicks,
            },
          ],
        },
        plugins: { streaming: false, zoom: false },
      },
    };
  }, [data, theme, windTurbine.name, windParam, powerParam]);

  const renderBody = () => {
    if (!windParam || !powerParam) {
      return <Message>This turbine type has no wind or power value to plot.</Message>;
    }
    if (error) {
      return <Message>Could not load the power curve.</Message>;
    }
    if (!chart) {
      return loading ? <Message>&nbsp;</Message> : <Message>No data in this period.</Message>;
    }
    return <Bar data={chart.data} options={chart.options} height={100} />;
  };

  return (
    <Wrapper>
      {renderBody()}
      <Controls>
        <Field>
          Start date
          <DatePicker
            selected={startDate}
            onChange={(d) => d && setStartDate(d)}
            minDate={moment(endDate).subtract(MAX_DAYS, "days").toDate()}
            maxDate={endDate}
            dateFormat="dd/MM/yyyy"
          />
        </Field>
        <Field>
          End date
          <DatePicker
            selected={endDate}
            onChange={(d) => {
              if (!d) return;
              setEndDate(d);
              if (moment(d).diff(startDate, "days") > MAX_DAYS) {
                setStartDate(moment(d).subtract(MAX_DAYS, "days").toDate());
              }
            }}
            minDate={startDate}
            maxDate={new Date()}
            dateFormat="dd/MM/yyyy"
          />
        </Field>
      </Controls>
      {loading && (
        <LoadingWrapper>
          <Loader active inline="centered" />
        </LoadingWrapper>
      )}
    </Wrapper>
  );
};

export default PowerCurve;
